import {ProvisionAccountKind} from './provision-account-kind.enum';
import {provisionEnumLabelFallback} from './provision-enum-label-fallback';

/**
 * German presentation companion of {@link ProvisionAccountKind} — the SINGLE place in the cp
 * frontend that NAMES an account, following the pattern of `provision-run-status-label.ts`.
 *
 * The labels are typed out here with real umlauts and match the correctly encoded backend
 * `@UiEnumValueInfo(uiLabel = …)` of `ProvisionAccountKind`.
 *
 * The map is keyed by the enum MEMBER, not by an ordinal literal: a map written with numeric keys
 * would silently survive a backend reordering, which is exactly the failure mode
 * 02-datenmodell.md §0.5 exists to prevent.
 */
export const CP_ACCOUNT_KIND_LABELS: Readonly<Record<ProvisionAccountKind, string>> = {
    [ProvisionAccountKind.PROVISION_ACCOUNT]: 'Provisionskonto',
    [ProvisionAccountKind.RESERVE_ACCOUNT]: 'Rücklagenkonto'
};

/** German label of an account; an unmapped constant stays VISIBLE — see {@link provisionEnumLabelFallback}. */
export function getProvisionAccountKindLabel(accountKind: ProvisionAccountKind): string {
    return CP_ACCOUNT_KIND_LABELS[accountKind]
        ?? provisionEnumLabelFallback(ProvisionAccountKind, accountKind);
}

/** One entry of {@link PROVISION_ACCOUNT_KIND_OPTIONS}. */
export interface ProvisionAccountKindOption {
    label: string;
    value: ProvisionAccountKind;
}

/**
 * The two accounts as a PrimeNG option list — for the `p-selectButton` of the account statement and
 * for every other account chooser that follows. Built FROM {@link CP_ACCOUNT_KIND_LABELS}, so an
 * option can never disagree with the heading rendered above the table it switches.
 *
 * Declaration order is the display order: the Provisionskonto is the deterministic entry point of
 * the statement (decision D3) and therefore comes first.
 */
export const PROVISION_ACCOUNT_KIND_OPTIONS: ProvisionAccountKindOption[] = [
    {label: CP_ACCOUNT_KIND_LABELS[ProvisionAccountKind.PROVISION_ACCOUNT], value: ProvisionAccountKind.PROVISION_ACCOUNT},
    {label: CP_ACCOUNT_KIND_LABELS[ProvisionAccountKind.RESERVE_ACCOUNT], value: ProvisionAccountKind.RESERVE_ACCOUNT}
];
